// file: client/src/offline/OfflineIndicator.jsx
/**
 * MODULE 09 — the unmistakable topbar pill (plan §8.1) + the outbox drawer
 * (§8.3). Token-styled, French, identical in both app shells.
 */
import * as React from "react";
import { Icon, Badge, Button } from "@/components/ui/primitives";
import { useOfflineStore } from "./offlineStore.js";
import { allOps, retryDeadLetter, discardDeadLetter, refreshCounts } from "./outbox.js";
import { drainOutbox } from "./syncEngine.js";

const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString("fr-DZ", { hour: "2-digit", minute: "2-digit" })
    : "—";

const OP_LABELS = {
  VENTE_LIBRE: "Vente libre",
  VENTE_CHIFA: "Vente CHIFA",
  CAISSE_CLOSE: "Clôture de caisse",
  STOCK_CORRECTION: "Correction de stock",
  CLIENT_CREATE: "Nouveau client",
};

export default function OfflineIndicator() {
  const status = useOfflineStore((s) => s.status);
  const pending = useOfflineStore((s) => s.pending);
  const deadLetters = useOfflineStore((s) => s.deadLetters);
  const lastSyncAt = useOfflineStore((s) => s.lastSyncAt);
  const serverEnabled = useOfflineStore((s) => s.serverEnabled);
  const device = useOfflineStore((s) => s.device);
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    if (open) allOps().then(setRows);
  }, [open, pending, deadLetters, status]);

  // Not enrolled / module off → render nothing (zero footprint).
  if (!serverEnabled || !device) return null;

  const offline = status === "OFFLINE";
  const syncing = status === "SYNCING";
  const label = offline
    ? "HORS LIGNE"
    : syncing
      ? "SYNCHRONISATION…"
      : "EN LIGNE";

  return (
    <div className="relative" data-offline-indicator>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={
          offline
            ? "Hors ligne — les opérations sont enregistrées localement"
            : `Dernière synchronisation : ${fmtTime(lastSyncAt)}`
        }
        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-2xs font-bold border transition-colors ${
          offline
            ? "bg-warning-soft text-warning border-warning"
            : syncing
              ? "bg-info-soft text-info border-info"
              : "bg-success-soft text-success border-transparent"
        }`}
        aria-live="polite"
      >
        <Icon name={offline ? "wifi-off" : syncing ? "refresh-cw" : "wifi"} size={12} />
        {label}
        {(pending > 0 || deadLetters > 0) && (
          <Badge tone={deadLetters ? "danger" : "warning"} size="sm">
            {pending + deadLetters}
          </Badge>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-[360px] bg-bg-elevated border border-border-subtle rounded-lg shadow-pop overflow-hidden z-40">
          <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-text-strong">
                File de synchronisation
              </div>
              <div className="text-2xs text-text-muted">
                Terminal « {device.label} » · dernière sync {fmtTime(lastSyncAt)}
              </div>
            </div>
            <Button
              variant="secondary"
              size="xs"
              icon="refresh-cw"
              disabled={offline || syncing || pending === 0}
              onClick={() => drainOutbox()}
            >
              Synchroniser
            </Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {rows.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-text-muted">
                Aucune opération en attente — tout est synchronisé.
              </div>
            ) : (
              rows.map((o) => (
                <div
                  key={o.id}
                  className="px-4 py-2.5 border-b border-border-subtle last:border-0 flex items-center gap-2"
                >
                  <Icon
                    name={o.statut === "failed" ? "alert-triangle" : "clock"}
                    size={13}
                    className={o.statut === "failed" ? "text-danger" : "text-warning"}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-text-strong truncate">
                      {OP_LABELS[o.op_type] ?? o.op_type}
                      {o.numero_provisoire && (
                        <span className="font-mono text-text-muted"> · {o.numero_provisoire}</span>
                      )}
                    </div>
                    <div className="text-2xs text-text-muted">
                      {fmtTime(o.created_at)}
                      {o.statut === "failed" && ` — échec définitif (${o.last_error ?? "?"})`}
                    </div>
                  </div>
                  {o.statut === "failed" && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={async () => {
                          await retryDeadLetter(o.id);
                          setRows(await allOps());
                        }}
                      >
                        Réessayer
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        className="text-danger"
                        onClick={async () => {
                          await discardDeadLetter(o.id);
                          await refreshCounts();
                          setRows(await allOps());
                        }}
                      >
                        Abandonner
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="px-4 py-2 bg-bg-sunken text-2xs text-text-muted">
            Les opérations hors ligne portent un N° provisoire (HL-…) régularisé
            à la synchronisation.
          </div>
        </div>
      )}
    </div>
  );
}
